// Validation for structured LLM outputs before anything they say reaches the sim or the screen: citation
// grounding (drop/flag unknown event ids) and order legality (re-run the sim's own `isLegal`, plus
// ownership/liveness checks an LLM could still get wrong even inside a valid schema). See spec §6
// "propose, vet, then act" / "grounded narration" and plan Task 16.
import * as audit from './audit';
import { card, isLegal } from '../sim/deck';
import type { Grid } from '../sim/grid';
import type { CardId, Graphic, Side, Target, Unit } from '../sim/types';
import type { PlaySchema } from './schemas';
import { graphicPoint, nearestPassableCell } from './templates';
import type { z } from 'zod';

export interface ValidOrder { unitId: number; card: CardId; target: Target }

/**
 * Anything that carries a list of proposed orders: a play, a parsed FRAGO wrapped in one, a fixture.
 *
 * Only the orders are re-checked here, so that is all the parameter asks for - and the open index
 * signature is deliberate, because `PlaySchema` has grown fields (`stance`, `disagreesWith`) that have
 * nothing to do with legality and a caller should not have to invent them to get an order vetted.
 */
export interface HasOrders {
  orders: z.infer<typeof PlaySchema>['orders'];
  [key: string]: unknown;
}

/**
 * Drops citations that do not name a known event id and flags the result when more than half of the
 * originally cited ids were invalid (spec §6 "grounded narration"). `known` should be the set of event
 * ids actually shown to the model in its prompt facts, not the whole log.
 */
export function validateCitations<T extends { cited_event_ids: number[] }>(
  v: T,
  known: Set<number>,
): { value: T; dropped: number; flagged: boolean } {
  const total = v.cited_event_ids.length;
  const kept = v.cited_event_ids.filter((id) => known.has(id));
  const dropped = total - kept.length;
  const flagged = total > 0 && dropped > total / 2;
  // If `v` is the object `callLLM` handed back, this validation belongs on that call's audit row.
  // Callers that validate a copy or a sub-object (the advisors, the debrief) miss here and record
  // their own outcome with `audit.note` instead - see `src/ai/audit.ts`.
  audit.note(audit.idOf(v), { citedTotal: total, citedDropped: dropped, flagged });
  return { value: { ...v, cited_event_ids: kept }, dropped, flagged };
}

/**
 * A graphic named as the target of a card that wants a point, rewritten into the point it meant.
 *
 * "Attack PL RED" and "fire mission on NAI 2" are how a human says it, and the model says it that way
 * too however firmly the prompt asks otherwise. A graphic *is* a place: the order is perfectly clear,
 * it was only shaped wrong, and rejecting it threw away most of what the advisors proposed. The aim
 * point is the same one the template plays use (the nearest point on a line, the centre of an area),
 * snapped to the nearest passable cell so a maneuver card can actually be given it.
 *
 * Only ever this direction. A point named as the target of a `:graphic` card is genuinely ambiguous -
 * which graphic? - so that one is still dropped, with its reason.
 */
export function coerceTarget(
  cardId: CardId,
  target: Target,
  unit: Unit,
  grid: Grid,
  graphics: Graphic[],
): Target {
  if (card(cardId).targetKind !== 'point') return target;
  if (target.kind !== 'graphic' || target.graphicId === undefined) return target;
  const g = graphics.find((x) => x.id === target.graphicId);
  if (!g || g.pts.length === 0) return target;
  return { kind: 'point', cell: nearestPassableCell(grid, graphicPoint(g, { x: unit.x, y: unit.y })) };
}

/**
 * Re-validates every order in a play against the sim's own legality rules plus ownership/liveness, which
 * a schema-valid LLM response can still violate (a made-up unit id, a dead unit, an enemy unit, a fire
 * mission from an armor platoon). Nothing an advisor proposes reaches the order book without passing
 * through here first.
 */
export function validatePlay(
  play: HasOrders,
  units: Unit[],
  side: Side,
  grid: Grid,
  graphics: Graphic[],
): { orders: ValidOrder[]; rejected: { order: unknown; reason: string }[] } {
  const orders: ValidOrder[] = [];
  const rejected: { order: unknown; reason: string }[] = [];

  for (const order of play.orders) {
    const unit = units.find((u) => u.id === order.unitId);
    if (!unit) { rejected.push({ order, reason: 'Unknown unit id' }); continue; }
    if (unit.side !== side) { rejected.push({ order, reason: 'Not a friendly unit' }); continue; }
    if (!unit.alive) { rejected.push({ order, reason: 'That unit is destroyed' }); continue; }

    const target = coerceTarget(order.card, order.target as Target, unit, grid, graphics);
    const legality = isLegal(order.card, unit, target, grid, graphics, units);
    if (!legality.ok) { rejected.push({ order, reason: legality.reason }); continue; }

    orders.push({ unitId: order.unitId, card: order.card, target });
  }

  return { orders, rejected };
}
